import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { Card, CardContent, Stack, Grid } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import i18n from 'simple-react-i18n'
import { exportFile } from 'utils/ExportDataUtil'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { getSetting } from 'utils/SettingUtils'
import useBoolean from 'utils/customHook/useBoolean'
import useActions from 'utils/customHook/useActions'
import { intersectionWith, isNil, compact, keys } from 'lodash'
import QualityAction from 'quality/actions/QualityAction'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import useTitle from 'utils/customHook/useTitle'
import AdministrationAction from 'administration/actions/AdministrationAction'
import SelectionTableModal from 'components/modal/SelectionTableModal'
import { formatParameterSelectedPopup } from 'campaign/utils/CampaignUtils'
import { normalizeString } from 'utils/StringUtil'
import Input from 'components/forms/Input'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import PropTypes from 'prop-types'
import { darkBlue, errorColor, whiteColor } from 'utils/constants/ColorTheme'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { hybridSearch } from 'utils/SearchUtil'
import { CATCHMENT_IRRELEVANT_PESTICIDES, CATCHMENT_LIST_SUM_PESTICIDES, CATCHMENT_PESTICIDES } from 'catchment/constants/CatchmentConstant'

const parameterFilterFunction = (list, { selection = '-1', listParam, searchValue }) => {
    const filterSelection = selection !== '-1' ? intersectionWith(list, listParam, (elem, id) => elem.id === id) : list
    const searchValueFormat = normalizeString(searchValue)
    return searchValue ? filterSelection.filter(p => p.labelSearch.includes(searchValueFormat)) : filterSelection
}

const ParameterFilterField = ({
    filter,
    setFilter,
}) => (
    <Grid container spacing={1} sx={{ paddingTop: '5px' }}>
        <Grid size={6}>
            <Input
                title={i18n.search}
                value={filter.searchValue}
                onChange={searchValue => setFilter(prev => ({ ...prev, searchValue }))}
            />
        </Grid>
        <Grid size={6}>
            <SelectionSelect
                value={filter.selection}
                onChange={(listParam, selection) => setFilter(prev => ({ ...prev, selection, listParam }))}
            />
        </Grid>
    </Grid>
)

ParameterFilterField.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        selection: PropTypes.string,
        listParam: PropTypes.arrayOf(PropTypes.string),
    }),
    setFilter: PropTypes.func,
}

const BoardsScreen = () =>{
    const {
        parameters,
        applicationSettings,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        applicationSettings: store.AdministrationReducer.applicationSettings,
    }), shallowEqual)

    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(QualityAction.fetchThresholds())
        dispatch(ParameterAction.fetchParameters())
    }, [])

    const {
        value: isReadMode,
        setTrue: setReadMode,
        setFalse: setEditMode,
    } = useBoolean(true)
    const {
        value: isListSumPopinOpen,
        setTrue: openListSumPopin,
        setFalse: closeListSumPopin,
    } = useBoolean(false)
    const {
        value: isRelevantPopinOpen,
        setTrue: openRelevantPopin,
        setFalse: closeRelevantPopin,
    } = useBoolean(false)
    const {
        value: isIrrelevantPopinOpen,
        setTrue: openIrrelevantPopin,
        setFalse: closeIrrelevantPopin,
    } = useBoolean(false)

    const getCatchmentListSumPesticides = getSetting(applicationSettings, CATCHMENT_LIST_SUM_PESTICIDES)?.split(',').filter(c => !!c) || []
    const getCatchmentRelevantPesticides = getSetting(applicationSettings, CATCHMENT_PESTICIDES)?.split(',').filter(c => !!c) || []
    const getCatchmentIrrelevantPesticides = getSetting(applicationSettings, CATCHMENT_IRRELEVANT_PESTICIDES)?.split(',').filter(c => !!c) || []

    const [listSumPesticides, setListSumPesticides] = useState(getCatchmentListSumPesticides)
    const [relevantPesticides, setRelevantPesticides] = useState(getCatchmentRelevantPesticides)
    const [irrelevantPesticides, setIrrelevantPesticides] = useState(getCatchmentIrrelevantPesticides)

    const listSumFormatted = useMemo(() => listSumPesticides.map(code => {
        const name = parameters.find(p => p.code === code)?.name
        return {
            code: { value: code },
            name: { value: name },
            _normalizedSearchString: [normalizeString(name), code],
        }
    }), [parameters, listSumPesticides])
    const relevantPesticidesFormatted = useMemo(() => relevantPesticides.map(code => {
        const name = parameters.find(p => p.code === code)?.name
        return {
            code: { value: code },
            name: { value: name },
            _normalizedSearchString: [normalizeString(name), code],
            colorLine: irrelevantPesticides.includes(code) ? errorColor : whiteColor,
        }
    }), [parameters, relevantPesticides, irrelevantPesticides])
    const irrelevantPesticidesFormatted = useMemo(() => irrelevantPesticides.map(code => {
        const name = parameters.find(p => p.code === code)?.name
        return {
            code: { value: code },
            name: { value: name },
            _normalizedSearchString: [normalizeString(name), code],
            colorLine: relevantPesticides.includes(code) ? errorColor : whiteColor,
        }
    }), [parameters, irrelevantPesticides, relevantPesticides])

    const [searchValue, setSearchValue] = useState('')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const setAllSettings = useCallback(() => {
        setListSumPesticides(getCatchmentListSumPesticides)
        setRelevantPesticides(getCatchmentRelevantPesticides)
        setIrrelevantPesticides(getCatchmentIrrelevantPesticides)
    }, [applicationSettings])

    useEffect(() => {
        setAllSettings()
    }, [setAllSettings])

    useTitle(() => {
        return [{
            title: i18n.catchments,
            href: 'catchment',
        }, {
            title: i18n.settings,
            href: 'catchment/option',
        }]
    }, [])

    useActions(() => {
        if (isReadMode) {
            return {
                edit: () => setEditMode(),
            }
        }
        return {
            cancel: () => {
                setAllSettings()
                setReadMode()
            },
            save: () => {
                const settings = [
                    { parameter: CATCHMENT_LIST_SUM_PESTICIDES, value: listSumPesticides.join(',') },
                    { parameter: CATCHMENT_PESTICIDES, value: relevantPesticides.join(',') },
                    { parameter: CATCHMENT_IRRELEVANT_PESTICIDES, value: irrelevantPesticides.join(',') },
                ].map(p => isNil(p.value) ? p : { ...p, value: `${p.value}` }).filter(p => getSetting(applicationSettings, p.parameter) !== p.value)
                if (settings.length) {
                    dispatch(AdministrationAction.updateSieauParameters(settings)).then(() => setReadMode())
                } else {
                    setReadMode()
                }
            },
        }
    }, [isReadMode, setAllSettings, listSumPesticides, relevantPesticides, irrelevantPesticides])

    const listSumFiltered = useMemo(() => hybridSearch(listSumFormatted, normalizeString(searchValue)), [listSumFormatted, searchValue])
    const relevantPesticidesFiltered = useMemo(() => {
        const redParameters = relevantPesticidesFormatted.filter(p => p.colorLine === errorColor)
        const otherParameters = relevantPesticidesFormatted.filter(p => p.colorLine !== errorColor)
        const sortRedParameters = hybridSearch(redParameters, normalizeString(searchValue), { threshold: 0 })
        const sortOtherParameters = hybridSearch(otherParameters, normalizeString(searchValue))
        return [...sortRedParameters, ...sortOtherParameters]
    }, [relevantPesticidesFormatted, searchValue])
    const irrelevantPesticidesFiltered = useMemo(() => {
        const redParameters = irrelevantPesticidesFormatted.filter(p => p.colorLine === errorColor)
        const otherParameters = irrelevantPesticidesFormatted.filter(p => p.colorLine !== errorColor)
        const sortRedParameters = hybridSearch(redParameters, normalizeString(searchValue), { threshold: 0 })
        const sortOtherParameters = hybridSearch(otherParameters, normalizeString(searchValue))
        return [...sortRedParameters, ...sortOtherParameters]
    }, [irrelevantPesticidesFormatted, searchValue])

    const activeParameters = useMemo(() => formatParameterSelectedPopup(parameters.filter(p => p.active), showSandreCode), [parameters, showSandreCode])

    return (
        <>
            <Card>
                <CardContent>
                    <Stack spacing={1}>
                        <Grid container>
                            <Grid size={4}>
                                <Input
                                    title={i18n.searchParameterInLists}
                                    value={searchValue}
                                    onChange={v => setSearchValue(v)}
                                    clearFunction
                                    data-cy='search'
                                />
                            </Grid>
                        </Grid>
                        <Grid container columnSpacing={2}>
                            <Grid size={6}>
                                <CardTable
                                    title={i18n.relevantPesticides}
                                    actions={[{
                                        icon: 'edit',
                                        tooltip: i18n.edit,
                                        onClick: openRelevantPopin,
                                        displayed: !isReadMode,
                                        color: whiteColor,
                                    }, {
                                        icon: 'file_download',
                                        tooltip: i18n.export,
                                        onClick: () => {
                                            const exportDataFormated = relevantPesticidesFormatted.map(p => (showSandreCode ? { code: p.code.value, name: p.name.value } : { name: p.name.value }))
                                            const exportData = exportDataFormated.length ? [{ ...exportDataFormated[0], headers: keys(exportDataFormated[0]) }, ...exportDataFormated.slice(1)] : []
                                            exportFile({
                                                data: exportData,
                                                exportType: 'xlsx',
                                                titleFile: 'relevantPesticides',
                                            })
                                        },
                                        color: whiteColor,
                                    }]}
                                    lineActions={[{
                                        icon: 'delete',
                                        onClick: pesticideToDelete => {
                                            setRelevantPesticides(prev =>
                                                prev.filter(p => p !== pesticideToDelete.code?.value),
                                            )
                                        },
                                        displayed: !isReadMode,
                                    }]}
                                    color={darkBlue}

                                    rows={relevantPesticidesFiltered}
                                    headers={compact([showSandreCode ? 'code' : '', 'name'])}
                                    maxHeight='25vh'
                                    rowsPerPageOptions={nbPerPageLabel}
                                />

                            </Grid>
                            <Grid size={6}>
                                <CardTable
                                    title={i18n.irrelevantPesticides}
                                    actions={[{
                                        icon: 'edit',
                                        tooltip: i18n.edit,
                                        onClick: openIrrelevantPopin,
                                        displayed: !isReadMode,
                                        color: whiteColor,
                                    }, {
                                        icon: 'file_download',
                                        tooltip: i18n.export,
                                        onClick: () => {
                                            const exportDataFormated = irrelevantPesticidesFormatted.map(p => (showSandreCode ? { code: p.code.value, name: p.name.value } : { name: p.name.value }))
                                            const exportData = exportDataFormated.length ? [{ ...exportDataFormated[0], headers: keys(exportDataFormated[0]) }, ...exportDataFormated.slice(1)] : []
                                            exportFile({
                                                data: exportData,
                                                exportType: 'xlsx',
                                                titleFile: 'irrelevantPesticides',
                                            })
                                        },
                                        color: whiteColor,
                                    }]}
                                    lineActions={[{
                                        icon: 'delete',
                                        onClick: pesticideToDelete => {
                                            setIrrelevantPesticides(prev =>
                                                prev.filter(p => p !== pesticideToDelete.code?.value),
                                            )
                                        },
                                        displayed: !isReadMode,
                                    }]}
                                    color={darkBlue}

                                    rows={irrelevantPesticidesFiltered}
                                    headers={compact([showSandreCode ? 'code' : '', 'name'])}
                                    maxHeight='25vh'
                                    rowsPerPageOptions={nbPerPageLabel}
                                />
                            </Grid>
                        </Grid>
                        <CardTable
                            title={i18n.listSumPesticides}
                            actions={[{
                                icon: 'edit',
                                tooltip: i18n.edit,
                                onClick: openListSumPopin,
                                displayed: !isReadMode,
                                color: whiteColor,

                            }, {
                                icon: 'file_download',
                                tooltip: i18n.export,
                                onClick: () => {
                                    const exportDataFormated = listSumFormatted.map(p => (showSandreCode ? { code: p.code.value, name: p.name.value } : { name: p.name.value }))
                                    const exportData = exportDataFormated.length ? [{ ...exportDataFormated[0], headers: keys(exportDataFormated[0]) }, ...exportDataFormated.slice(1)] : []
                                    exportFile({
                                        data: exportData,
                                        exportType: 'xlsx',
                                        titleFile: 'list_sum_pesticides',
                                    })
                                },
                                color: whiteColor,
                            }]}
                            lineActions={[{
                                icon: 'delete',
                                onClick: pesticideToDelete => {
                                    setListSumPesticides(prev =>
                                        prev.filter(p => p !== pesticideToDelete.code?.value),
                                    )
                                },
                                displayed: !isReadMode,
                            }]}
                            color={darkBlue}

                            rows={listSumFiltered}
                            headers={compact([showSandreCode? 'code' : '', 'name'])}
                            maxHeight='25vh'
                            rowsPerPageOptions={nbPerPageLabel}
                        />
                    </Stack>
                </CardContent>
            </Card>
            <SelectionTableModal
                isOpen={isListSumPopinOpen}
                onClose={closeListSumPopin}
                onValidate={list => {
                    closeListSumPopin()
                    setListSumPesticides(list)
                }}
                title={i18n.listSumPesticidesSelection}

                listData={activeParameters}
                defaultSelectionList={listSumPesticides}

                listHeaders={['name', 'type', 'nature', 'status']}
                listTitle={i18n.nonSelectedParameters}
                selectionListTitle={i18n.selectedParameters}
                maxHeightTable={'53vh'}

                filterField={ParameterFilterField}
                filterFunction={parameterFilterFunction}
            />
            <SelectionTableModal
                isOpen={isRelevantPopinOpen}
                onClose={closeRelevantPopin}
                onValidate={list => {
                    closeRelevantPopin()
                    setRelevantPesticides(list)
                }}
                title={i18n.parametersSelection}

                listData={activeParameters}
                defaultSelectionList={relevantPesticides}

                listHeaders={['name', 'type', 'nature', 'status']}
                listTitle={i18n.nonSelectedParameters}
                selectionListTitle={i18n.selectedParameters}
                maxHeightTable={'53vh'}

                filterField={ParameterFilterField}
                filterFunction={parameterFilterFunction}
            />
            <SelectionTableModal
                isOpen={isIrrelevantPopinOpen}
                onClose={closeIrrelevantPopin}
                onValidate={list => {
                    closeIrrelevantPopin()
                    setIrrelevantPesticides(list)
                }}
                title={i18n.parametersSelection}

                listData={activeParameters}
                defaultSelectionList={irrelevantPesticides}

                listHeaders={['name', 'type', 'nature', 'status']}
                listTitle={i18n.nonSelectedParameters}
                selectionListTitle={i18n.selectedParameters}
                maxHeightTable={'53vh'}

                filterField={ParameterFilterField}
                filterFunction={parameterFilterFunction}
            />
        </>
    )
}
export default BoardsScreen